/**
 * Realistic per-network post previews, shared between the composer
 * (PostCreate) and the client approval review page (ApprovalReview).
 *
 * The types here are intentionally minimal/structural so callers can pass their
 * own richer account/media objects without conversion.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Image as ImageIcon,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  MoreHorizontal,
  Repeat2,
  BarChart3,
  Globe,
  ThumbsUp,
  Music,
  ChevronLeft,
  ChevronRight,
  Volume2,
  Search,
  Home,
  Compass,
  PlusSquare,
  User,
  Film,
  X,
} from 'lucide-react';
import { FaBluesky, FaPinterest } from 'react-icons/fa6';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { cn } from '../lib/cn';

export type PreviewKind = 'post' | 'reel' | 'story' | 'video' | 'short' | 'pin' | 'photo';

export type PreviewAccount = {
  platform: Platform;
  username?: string | null;
  displayName?: string | null;
  profilePhotoUrl?: string | null;
  isPremium?: boolean;
};

export type PreviewMedia = {
  url: string;
  kind: 'image' | 'video';
};

const KIND_LABEL: Record<PreviewKind, string> = {
  post: 'Post',
  reel: 'Reel',
  story: 'Story',
  video: 'Vídeo',
  short: 'Short',
  pin: 'Pin',
  photo: 'Foto',
};

export function XVerifiedBadge({ size = 14 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-label="Cuenta verificada" className="shrink-0">
      <path
        fill="#1d9bf0"
        d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.36-6.2 6.77z"
      />
    </svg>
  );
}

/* shared bits */
function Avatar({ account, size = 32 }: { account: PreviewAccount; size?: number }) {
  const initial = (account.displayName ?? account.username ?? '?').charAt(0).toUpperCase();
  return account.profilePhotoUrl ? (
    <img
      src={account.profilePhotoUrl}
      alt=""
      style={{ width: size, height: size }}
      className="rounded-full object-cover"
    />
  ) : (
    <span
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      className="rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-white font-bold flex items-center justify-center"
    >
      {initial}
    </span>
  );
}

/**
 * Instagram feed allows aspect ratios from 1.91:1 (landscape) to 3:4 (portrait,
 * 1080×1440). Expressed as width/height. Keep in sync with the publishing limit
 * in server/src/services/instagramMediaService.ts.
 */
const IG_FEED_RATIO: [number, number] = [0.75, 1.91];

function MediaSlide({ media, aspect = 'aspect-square', objectClass = 'object-cover', adaptiveRange }: {
  media: PreviewMedia[];
  aspect?: string;
  objectClass?: string;
  /** When set, the container adapts to the first media's real ratio (w/h), clamped to [min,max]. */
  adaptiveRange?: [number, number];
}) {
  const [idx, setIdx] = useState(0);
  const [ratio, setRatio] = useState<number | null>(null);
  useEffect(() => { if (idx >= media.length) setIdx(0); }, [media.length, idx]);

  // Measure the first media's natural ratio so the frame matches how the network
  // would actually display it (no forced square crop for portraits).
  const first = media[0];
  const firstUrl = first?.url;
  const firstKind = first?.kind;
  useEffect(() => {
    if (!adaptiveRange || !firstUrl) { setRatio(null); return; }
    let cancelled = false;
    const apply = (w: number, h: number) => {
      if (cancelled || !w || !h) return;
      setRatio(Math.min(adaptiveRange[1], Math.max(adaptiveRange[0], w / h)));
    };
    if (firstKind === 'image') {
      const img = new Image();
      img.onload = () => apply(img.naturalWidth, img.naturalHeight);
      img.src = firstUrl;
    } else {
      const v = document.createElement('video');
      v.onloadedmetadata = () => apply(v.videoWidth, v.videoHeight);
      v.src = firstUrl;
    }
    return () => { cancelled = true; };
  }, [adaptiveRange, firstUrl, firstKind]);

  const m = media[idx];

  if (!m) {
    return (
      <div
        className={cn('w-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400 text-xs', !ratio && aspect)}
        style={ratio ? { aspectRatio: String(ratio) } : undefined}
      >
        <ImageIcon size={20} className="mr-1.5" /> Sin media
      </div>
    );
  }

  return (
    <div
      className={cn('relative w-full bg-black', !ratio && aspect)}
      style={ratio ? { aspectRatio: String(ratio) } : undefined}
    >
      {m.kind === 'image' ? (
        <img src={m.url} alt="" className={cn('w-full h-full', objectClass)} />
      ) : (
        <video src={m.url} className={cn('w-full h-full', objectClass)} controls={false} autoPlay muted loop playsInline />
      )}
      {media.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + media.length) % media.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % media.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
          >
            <ChevronRight size={14} />
          </button>
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/50 text-white text-[10px] px-2 py-0.5 font-medium">
            {idx + 1}/{media.length}
          </div>
        </>
      )}
    </div>
  );
}

function CaptionText({ text, clampChars }: { text: string; clampChars: number }) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= clampChars) return <>{text}</>;
  if (expanded) {
    return (
      <>
        {text}{' '}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(false); }}
          className="text-slate-400 hover:text-slate-600 font-medium underline-offset-2 hover:underline"
        >
          Mostrar menos
        </button>
      </>
    );
  }
  return (
    <>
      {text.slice(0, clampChars)}
      {'… '}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(true); }}
        className="text-slate-500 hover:text-slate-700 font-medium underline-offset-2 hover:underline"
      >
        más
      </button>
    </>
  );
}

function IconStat({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center">
      {icon}
      <span className="text-[10px] mt-0.5">{label}</span>
    </div>
  );
}

/* ─── INSTAGRAM POST ─────────────────────────────────────── */
function InstagramPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const username = account.username ?? account.displayName ?? 'tu_marca';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Helvetica,Arial,sans-serif] text-[14px] text-slate-900">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="relative inline-flex p-[2px] rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-fuchsia-600">
          <Avatar account={account} size={30} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold truncate">{username}</div>
          <div className="text-[11px] text-slate-500 truncate">Madrid · Original audio</div>
        </div>
        <MoreHorizontal size={18} className="text-slate-700" />
      </div>
      <MediaSlide media={media} aspect="aspect-square" adaptiveRange={IG_FEED_RATIO} />
      <div className="px-3 pt-3 pb-2 flex items-center gap-4 text-slate-900">
        <Heart size={22} strokeWidth={1.75} />
        <MessageCircle size={22} strokeWidth={1.75} />
        <Share2 size={22} strokeWidth={1.75} />
        <Bookmark size={22} strokeWidth={1.75} className="ml-auto" />
      </div>
      <div className="px-3 text-[13px] font-semibold">1.248 me gusta</div>
      {caption && (
        <div className="px-3 pt-1 pb-2 text-[13px] leading-snug">
          <span className="font-semibold mr-1.5">{username}</span>
          <CaptionText text={caption} clampChars={150} />
        </div>
      )}
      <div className="px-3 pb-1 text-[11px] text-slate-400">Ver los 32 comentarios</div>
      <div className="px-3 pb-2.5 text-[10px] text-slate-400 uppercase tracking-wide">Hace 1 min</div>
    </div>
  );
}

/* ─── FACEBOOK POST ──────────────────────────────────────── */
function FacebookPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const name = account.displayName ?? account.username ?? 'Tu página';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Helvetica,Arial,sans-serif] text-[14px] text-slate-900 shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <Avatar account={account} size={36} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold truncate">{name}</div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            Hace 1 min · <Globe size={11} />
          </div>
        </div>
        <MoreHorizontal size={18} className="text-slate-500" />
      </div>
      {caption && (
        <div className="px-3 pb-2 text-[14px] leading-snug">
          <CaptionText text={caption} clampChars={220} />
        </div>
      )}
      <MediaSlide media={media} aspect="aspect-[4/3]" />
      <div className="px-3 py-2 flex items-center justify-between text-[12px] text-slate-500">
        <div className="flex items-center -space-x-1">
          <span className="h-4 w-4 rounded-full bg-[#1877F2] inline-flex items-center justify-center text-white text-[10px]">👍</span>
          <span className="h-4 w-4 rounded-full bg-[#F33E58] inline-flex items-center justify-center text-white text-[10px]">❤</span>
          <span className="ml-1">428</span>
        </div>
        <div>32 comentarios · 18 veces compartido</div>
      </div>
      <div className="px-3 py-1.5 border-t border-slate-100 flex items-center justify-around text-[12px] text-slate-600 font-medium">
        <span className="flex items-center gap-1.5 py-1"><ThumbsUp size={15} /> Me gusta</span>
        <span className="flex items-center gap-1.5 py-1"><MessageCircle size={15} /> Comentar</span>
        <span className="flex items-center gap-1.5 py-1"><Share2 size={15} /> Compartir</span>
      </div>
    </div>
  );
}

/* ─── X / TWITTER POST ────────────────────────────────────── */
function XPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const name = account.displayName ?? account.username ?? 'Tu marca';
  const handle = account.username ?? 'tumarca';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Helvetica,Arial,sans-serif] text-[14px] text-slate-900">
      <div className="flex gap-2.5 p-3">
        <Avatar account={account} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 text-[14px] font-bold leading-tight">
                <span className="truncate">{name}</span>
                {account.isPremium && <XVerifiedBadge size={15} />}
              </div>
              <div className="flex items-center gap-1 text-[13px] text-slate-500 leading-tight">
                <span className="truncate">@{handle}</span>
                <span className="shrink-0">· 1 min</span>
              </div>
            </div>
            <MoreHorizontal size={16} className="text-slate-500 shrink-0 mt-0.5" />
          </div>
          {caption && (
            <div className="mt-1 text-[14px] leading-snug whitespace-pre-wrap break-words">
              <CaptionText text={caption} clampChars={280} />
            </div>
          )}
          {media.length > 0 && (
            <div className="mt-2 rounded-2xl overflow-hidden border border-slate-200">
              <MediaSlide media={media} aspect="aspect-[16/10]" />
            </div>
          )}
          <div className="mt-2 flex items-center justify-between text-slate-500 text-[12px]">
            <span className="inline-flex items-center gap-1"><MessageCircle size={15} /> 24</span>
            <span className="inline-flex items-center gap-1"><Repeat2 size={15} /> 18</span>
            <span className="inline-flex items-center gap-1"><Heart size={15} /> 142</span>
            <span className="inline-flex items-center gap-1"><BarChart3 size={15} /> 2.1K</span>
            <Share2 size={15} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── LINKEDIN POST ──────────────────────────────────────── */
function LinkedInPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const name = account.displayName ?? account.username ?? 'Tu marca';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Helvetica,Arial,sans-serif] text-[14px] text-slate-900">
      <div className="flex items-start gap-2 px-3 py-2.5">
        <Avatar account={account} size={42} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold truncate">{name}</div>
          <div className="text-[11px] text-slate-500 truncate">SaaS · Marketing · Growth</div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            1 min · <Globe size={10} />
          </div>
        </div>
        <MoreHorizontal size={18} className="text-slate-500" />
      </div>
      {caption && (
        <div className="px-3 pb-2 text-[14px] leading-snug whitespace-pre-wrap">
          <CaptionText text={caption} clampChars={280} />
        </div>
      )}
      {media.length > 0 && <MediaSlide media={media} aspect="aspect-[4/3]" />}
      <div className="px-3 py-2 flex items-center justify-between text-[11px] text-slate-500 border-b border-slate-100">
        <div className="flex items-center -space-x-1">
          <span className="h-4 w-4 rounded-full bg-[#0A66C2] inline-flex items-center justify-center text-white text-[9px]">👍</span>
          <span className="h-4 w-4 rounded-full bg-[#E04F5F] inline-flex items-center justify-center text-white text-[9px]">❤</span>
          <span className="h-4 w-4 rounded-full bg-[#7A6D32] inline-flex items-center justify-center text-white text-[9px]">💡</span>
          <span className="ml-1">312</span>
        </div>
        <span>24 comentarios · 8 republicaciones</span>
      </div>
      <div className="px-3 py-1 flex items-center justify-around text-[12px] text-slate-600 font-medium">
        <span className="flex items-center gap-1.5 py-1"><ThumbsUp size={15} /> Recomendar</span>
        <span className="flex items-center gap-1.5 py-1"><MessageCircle size={15} /> Comentar</span>
        <span className="flex items-center gap-1.5 py-1"><Repeat2 size={15} /> Republicar</span>
      </div>
    </div>
  );
}

/* ─── THREADS POST ──────────────────────────────────────── */
function ThreadsPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const handle = account.username ?? 'tu_marca';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Helvetica,Arial,sans-serif] text-[14px] text-slate-900">
      <div className="flex gap-2.5 p-3">
        <Avatar account={account} size={38} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[13px]">
            <span className="font-bold truncate">{handle}</span>
            <span className="text-slate-500 ml-auto">1 min</span>
            <MoreHorizontal size={16} className="text-slate-500" />
          </div>
          {caption && (
            <div className="mt-0.5 text-[14px] leading-snug whitespace-pre-wrap">
              <CaptionText text={caption} clampChars={500} />
            </div>
          )}
          {media.length > 0 && (
            <div className="mt-2 rounded-2xl overflow-hidden border border-slate-200">
              <MediaSlide media={media} aspect="aspect-square" />
            </div>
          )}
          <div className="mt-2 flex items-center gap-4 text-slate-700">
            <Heart size={18} strokeWidth={1.75} />
            <MessageCircle size={18} strokeWidth={1.75} />
            <Repeat2 size={18} strokeWidth={1.75} />
            <Share2 size={18} strokeWidth={1.75} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── BLUESKY POST ──────────────────────────────────────── */
function BlueskyPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const name = account.displayName ?? account.username ?? 'Tu marca';
  const handle = account.username ?? 'tu.bsky.social';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Helvetica,Arial,sans-serif] text-[14px] text-slate-900">
      <div className="flex gap-2.5 p-3">
        <Avatar account={account} size={38} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-[13px] flex-wrap">
            <span className="font-bold truncate">{name}</span>
            <span className="text-slate-500 truncate text-[12px]">@{handle}</span>
            <span className="text-slate-500 text-[12px]">· 1 min</span>
            <FaBluesky className="ml-auto text-[#0085FF]" size={14} />
          </div>
          {caption && (
            <div className="mt-0.5 text-[14px] leading-snug whitespace-pre-wrap">
              <CaptionText text={caption} clampChars={300} />
            </div>
          )}
          {media.length > 0 && (
            <div className="mt-2 rounded-xl overflow-hidden border border-slate-200">
              <MediaSlide media={media} aspect="aspect-[16/10]" />
            </div>
          )}
          <div className="mt-2 flex items-center gap-5 text-slate-500 text-[12px]">
            <span className="inline-flex items-center gap-1"><MessageCircle size={14} /> 5</span>
            <span className="inline-flex items-center gap-1"><Repeat2 size={14} /> 12</span>
            <span className="inline-flex items-center gap-1"><Heart size={14} /> 48</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── PINTEREST PIN ──────────────────────────────────────── */
function PinterestPinPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const name = account.displayName ?? account.username ?? 'Tu marca';
  return (
    <div className="w-full max-w-[280px] bg-white rounded-2xl overflow-hidden shadow-md border border-slate-200">
      <div className="relative">
        <MediaSlide media={media} aspect="aspect-[3/4]" />
        <button className="absolute top-2 right-2 bg-[#E60023] text-white text-[12px] font-bold px-3 py-1.5 rounded-full shadow">
          Guardar
        </button>
      </div>
      <div className="p-3">
        {caption && (
          <div className="text-[13px] font-semibold text-slate-900 leading-snug line-clamp-2 mb-2">
            <CaptionText text={caption} clampChars={100} />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Avatar account={account} size={26} />
          <span className="text-[12px] font-medium text-slate-700 truncate">{name}</span>
          <FaPinterest className="ml-auto text-[#BD081C]" size={14} />
        </div>
      </div>
    </div>
  );
}

/* ─── YOUTUBE VIDEO (horizontal) ─────────────────────────── */
function YouTubeVideoPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const name = account.displayName ?? account.username ?? 'Tu canal';
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden font-[Roboto,Arial,sans-serif] text-[13px] text-slate-900">
      <div className="relative">
        <MediaSlide media={media} aspect="aspect-video" />
        <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] px-1 py-0.5 rounded">0:30</span>
      </div>
      <div className="p-3 flex gap-2">
        <Avatar account={account} size={36} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold leading-snug line-clamp-2">
            <CaptionText text={caption || 'Título de tu vídeo'} clampChars={80} />
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5 truncate">{name}</div>
          <div className="text-[12px] text-slate-500">1.2K visitas · hace 1 min</div>
        </div>
      </div>
    </div>
  );
}

/* ─── GENERIC FALLBACK ──────────────────────────────────── */
function GenericPostPreview({ account, media, caption }: { account: PreviewAccount; media: PreviewMedia[]; caption: string }) {
  const meta = PLATFORM_META[account.platform];
  return (
    <div className="w-full max-w-[340px] bg-white rounded-md border border-slate-200 overflow-hidden text-[14px] text-slate-900">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <Avatar account={account} size={32} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold truncate">{account.displayName ?? account.username ?? meta.label}</div>
          <div className="text-[11px] text-slate-500">{meta.label} · ahora</div>
        </div>
      </div>
      {media.length > 0 && <MediaSlide media={media} aspect="aspect-square" />}
      {caption && (
        <div className="px-3 py-2 text-[13px] leading-snug">
          <CaptionText text={caption} clampChars={200} />
        </div>
      )}
    </div>
  );
}

/* ─── REEL / SHORT / TIKTOK (vertical) ───────────────────── */
function ReelLikePreview({ account, media, caption, kind }: {
  account: PreviewAccount;
  media: PreviewMedia[];
  caption: string;
  kind: PreviewKind;
}) {
  const platform = account.platform;
  const isTikTok = platform === 'tiktok' || platform === 'tiktok_business';
  const isYouTube = platform === 'youtube';
  const isFacebook = platform === 'facebook';
  const username = account.username ?? account.displayName ?? 'tu_marca';
  const badgeMeta = PLATFORM_META[platform];

  return (
    <div className="relative w-full max-w-[260px] aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-xl text-white font-[Helvetica,Arial,sans-serif]">
      <MediaSlide media={media} aspect="aspect-[9/16]" objectClass="object-cover" />
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
      <div className="absolute top-0 inset-x-0 px-3 pt-3 flex items-center justify-between text-[13px] font-semibold">
        {isTikTok ? (
          <div className="flex gap-4 mx-auto">
            <span className="opacity-70">Siguiendo</span>
            <span className="border-b-2 border-white pb-0.5">Para ti</span>
          </div>
        ) : isYouTube ? (
          <>
            <span className="font-bold">Shorts</span>
            <Search size={18} />
          </>
        ) : (
          <>
            <span>{kind === 'reel' ? 'Reels' : KIND_LABEL[kind]}</span>
            <span><MoreHorizontal size={18} /></span>
          </>
        )}
      </div>
      <div className="absolute right-2 bottom-20 flex flex-col items-center gap-3.5">
        <div className="relative">
          <Avatar account={account} size={36} />
          {!isYouTube && (
            <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#FE2C55] text-white text-[10px] font-bold">+</span>
          )}
        </div>
        <IconStat icon={<Heart size={26} fill="currentColor" />} label="12.4K" />
        <IconStat icon={<MessageCircle size={26} fill="currentColor" />} label="328" />
        {isTikTok ? (
          <>
            <IconStat icon={<Bookmark size={26} fill="currentColor" />} label="1.2K" />
            <IconStat icon={<Share2 size={26} />} label="218" />
          </>
        ) : (
          <IconStat icon={<Share2 size={26} />} label="Enviar" />
        )}
        <div className="h-9 w-9 rounded-md bg-white/10 border border-white/30 flex items-center justify-center">
          {isTikTok ? <Music size={14} /> : <Volume2 size={14} />}
        </div>
      </div>
      <div className="absolute left-3 right-16 bottom-3">
        <div className="text-[13px] font-bold">@{username}</div>
        {caption && (
          <div className="text-[12px] leading-snug mt-1 line-clamp-3">
            <CaptionText text={caption} clampChars={120} />
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] opacity-90">
          <Music size={11} />
          <span className="truncate">Sonido original · {username}</span>
        </div>
      </div>
      {isTikTok ? (
        <div className="absolute bottom-0 inset-x-0 h-9 bg-black flex items-center justify-around text-white/80 text-[10px]">
          <Home size={14} />
          <Compass size={14} />
          <PlusSquare size={16} />
          <MessageCircle size={14} />
          <User size={14} />
        </div>
      ) : isFacebook ? (
        <div className="absolute top-3 left-3 text-[11px] font-bold bg-black/40 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
          <Film size={10} /> Reels
        </div>
      ) : null}
      <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] bg-black/40 rounded-full px-1.5 py-0.5">
        <badgeMeta.icon size={10} />
      </div>
    </div>
  );
}

/* ─── STORY (Instagram / Facebook) ───────────────────────── */
function StoryPreview({ account, media }: { account: PreviewAccount; media: PreviewMedia[] }) {
  const username = account.username ?? account.displayName ?? 'tu_marca';
  const meta = PLATFORM_META[account.platform];
  return (
    <div className="relative w-full max-w-[260px] aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-xl text-white font-[Helvetica,Arial,sans-serif]">
      <MediaSlide media={media} aspect="aspect-[9/16]" objectClass="object-cover" />
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
      <div className="absolute top-2 left-2 right-2 flex gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
            <div className={cn('h-full bg-white', i === 0 ? 'w-2/3' : 'w-0')} />
          </div>
        ))}
      </div>
      <div className="absolute top-5 left-2 right-2 flex items-center gap-2">
        <Avatar account={account} size={26} />
        <span className="text-[12px] font-semibold truncate">{username}</span>
        <span className="text-[11px] text-white/70">ahora</span>
        <meta.icon size={12} className="ml-auto opacity-80" />
        <X size={16} className="opacity-80" />
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
        <div className="flex-1 h-9 rounded-full border border-white/40 px-3 flex items-center text-[11px] text-white/70">
          Enviar un mensaje
        </div>
        <Heart size={20} />
        <Share2 size={20} />
      </div>
    </div>
  );
}

/* ─── Router ─────────────────────────────────────────────── */
export function PlatformPreview({ account, kind, media, caption }: {
  account: PreviewAccount;
  kind: PreviewKind;
  media: PreviewMedia[];
  caption: string;
}) {
  if (kind === 'story') {
    return <StoryPreview account={account} media={media} />;
  }
  const isTikTokAcc = account.platform === 'tiktok' || account.platform === 'tiktok_business';
  if (kind === 'reel' || kind === 'short' || ((kind === 'video' || kind === 'photo') && isTikTokAcc)) {
    return <ReelLikePreview account={account} media={media} caption={caption} kind={kind} />;
  }
  switch (account.platform) {
    case 'instagram': return <InstagramPostPreview account={account} media={media} caption={caption} />;
    case 'facebook':  return <FacebookPostPreview account={account} media={media} caption={caption} />;
    case 'twitter':   return <XPostPreview account={account} media={media} caption={caption} />;
    case 'linkedin':  return <LinkedInPostPreview account={account} media={media} caption={caption} />;
    case 'threads':   return <ThreadsPostPreview account={account} media={media} caption={caption} />;
    case 'bluesky':   return <BlueskyPostPreview account={account} media={media} caption={caption} />;
    case 'pinterest': return <PinterestPinPreview account={account} media={media} caption={caption} />;
    case 'youtube':   return <YouTubeVideoPreview account={account} media={media} caption={caption} />;
    default:          return <GenericPostPreview account={account} media={media} caption={caption} />;
  }
}
